interface BreakdownRow {
  label: string;
  value: number;
  /** A second figure shown after the value, e.g. a cost next to a count. */
  detail?: string;
}

interface BreakdownBarsProps {
  rows: BreakdownRow[];
  format: (value: number) => string;
  emptyText?: string;
}

/**
 * A ranked list with proportional bars. Reads faster than a pie for the
 * handful of categories these breakdowns have, and keeps exact numbers visible.
 */
export function BreakdownBars({
  rows,
  format,
  emptyText = "Nothing to show."
}: BreakdownBarsProps) {
  const sorted = [...rows].filter((r) => r.value > 0).sort((a, b) => b.value - a.value);
  const max = sorted[0]?.value ?? 0;
  const total = sorted.reduce((sum, r) => sum + r.value, 0);

  if (sorted.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ul className="space-y-3">
      {sorted.map((row) => (
        <li key={row.label} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{row.label}</span>
            <span className="shrink-0 tabular-nums">
              {format(row.value)}
              <span className="ml-2 text-xs text-muted-foreground">
                {row.detail ?? `${Math.round((row.value / total) * 100)}%`}
              </span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted">
            <div
              className="h-2 rounded-full bg-chart-1"
              style={{ width: `${max ? (row.value / max) * 100 : 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
